import type { Booking } from "@/types";
import { parseISODate, startOfDay } from "@/lib/date";

export type TripPhase = "upcoming" | "past";

export interface StatusMeta {
  label: string;
  short: string;
  icon: string;
  className: string;
  dot: string;
}

const STATUS_META: Record<string, StatusMeta> = {
  pending: {
    label: "Awaiting M-Pesa payment",
    short: "Pending payment",
    icon: "ri-time-line",
    className: "bg-primary-500/12 text-primary-400",
    dot: "bg-primary-400",
  },
  confirmed: {
    label: "Confirmed & paid",
    short: "Confirmed",
    icon: "ri-checkbox-circle-line",
    className: "bg-accent-500/15 text-accent-300",
    dot: "bg-accent-400",
  },
  ongoing: {
    label: "Trip in progress",
    short: "On trip",
    icon: "ri-roadster-line",
    className: "bg-accent-500/15 text-accent-300",
    dot: "bg-accent-400",
  },
  completed: {
    label: "Trip completed",
    short: "Completed",
    icon: "ri-flag-2-line",
    className: "bg-background-200/70 text-foreground-800",
    dot: "bg-foreground-500",
  },
  cancelled: {
    label: "Booking cancelled",
    short: "Cancelled",
    icon: "ri-close-circle-line",
    className: "bg-secondary-950/60 text-secondary-300",
    dot: "bg-secondary-400",
  },
};

export function statusMeta(status: string | null | undefined): StatusMeta {
  return STATUS_META[status || "pending"] || STATUS_META.pending;
}

export function tripPhase(booking: Pick<Booking, "end_date">): TripPhase {
  if (!booking.end_date) return "upcoming";
  const end = startOfDay(parseISODate(booking.end_date));
  const today = startOfDay(new Date());
  return end < today ? "past" : "upcoming";
}

export function isAwaitingPayment(status: string | null | undefined): boolean {
  return !status || status === "pending";
}

export function isCancelled(status: string | null | undefined): boolean {
  return status === "cancelled";
}

export function countDaysLabel(days: number | null | undefined): string {
  const value = Number(days ?? 0);
  return `${value} ${value === 1 ? "day" : "days"}`;
}

export function canLeaveReview(
  booking: Pick<Booking, "status" | "end_date">
): boolean {
  if (booking.status === "cancelled" || booking.status === "pending") return false;
  return booking.status === "completed" || tripPhase(booking) === "past";
}